import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, of } from 'rxjs'
import { MainViewActions } from './action-types'
import { catchError, concatMap, map, switchMap, take } from 'rxjs/operators'
import { ContractBulkActionControllerService } from '../../../api/backend'
import { AuthContextStorageService } from '../../../core/auth/auth-context-storage.service'
import {
    selectBulkOperationUpdate,
    selectMessages,
    selectMessagesAndCuzkPasswordValidityState,
    selectShowBulkOperations,
    selectUnreadNotificationsCount,
} from './main-view.selectors'
import { MainDialogService } from '../services/main-dialog.service'
import _ from 'lodash'
import { addDays, format, parseISO } from 'date-fns'

import { BarterActions } from '../../../features/barter/state/action-types'
import { SelectedDepartmentService } from '../../services/selected-department.service'
import { HeaderActions } from '../header/state/action-types'
import { StoreKeysEnum } from '../../../store/store-keys'
import { NonCadastralContractCreateActions } from '../../../features/non-cadastral/non-cadastral-contract/create/state/action-types'
import { CaseEditActions } from '../../../features/purchase-case/edit/state/action-types'
import { ContractEditActions } from '../../../features/contracts/contract/edit/state/action-types'
import { SubjectShowActions } from '../../../features/subjects/show/state/action-types'
import { SubcontractEditActions } from '../../../features/contracts/subcontract/edit/state/action-types'

@Injectable()
export class MainViewEffects {

    runBulkOperationUpdate$: Observable<any>
    setNotificationDetail$: Observable<any>
    setAllIsEditingFalse$: Observable<any>
    setBlockRequests$: Observable<any>
    setCuzkPasswordValidityState$: Observable<any>
    setSelectedDepartment$: Observable<any>
    setUnreadNotificationsCount$: Observable<any>
    setSkippedDateDueCuzkPasswordValidity$: Observable<any>
    toggleShowBulkOperations$: Observable<any>
    toggleShowBulkOperationsOff$: Observable<any>

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(Store) private store: Store,
                @Inject(SelectedDepartmentService) private selectedDepartmentService: SelectedDepartmentService,
                @Inject(MainDialogService) private mainDialogService: MainDialogService,
                @Inject(ContractBulkActionControllerService) private contractBulkActionControllerService: ContractBulkActionControllerService,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
                ) {

        this.runBulkOperationUpdate$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.runBulkOperationUpdate),
            switchMap(() => {
                this.store.dispatch(MainViewActions.setContractBulkOperationLoading({
                    loading: true
                }))

                return this.contractBulkActionControllerService.getLastActions(
                    this.authContextStorageService.getProjectKey(),
                ).pipe(
                    concatMap(data => {
                         return [
                            MainViewActions.setBulkOperations({
                                bulkOperations: data.items
                            }),
                            MainViewActions.setContractBulkOperationLoading({
                                loading: false
                            })
                        ]
                    }),
                    catchError(error => {
                        console.warn('runBulkOperationUpdate$ failed:', error?.status)

                        return of(MainViewActions.setContractBulkOperationLoading({
                            loading: false
                        }))
                    })
                )
            })
        ) })

        this.toggleShowBulkOperations$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.toggleShowBulkOperations),
            switchMap(() => {
                return this.store.select(selectShowBulkOperations).pipe(
                    take(1),
                    switchMap(show => {
                        const newShowBulkOperations = !show

                        this.store.dispatch(MainViewActions.setShowBulkOperations({
                            showBulkOperations: newShowBulkOperations
                        }))

                        return this.store.select(selectBulkOperationUpdate).pipe(
                            take(1),
                            map(bulkOperationUpdate => {
                                if (newShowBulkOperations) {
                                    this.store.dispatch(MainViewActions.runBulkOperationUpdate())

                                    if (!bulkOperationUpdate) {
                                        return MainViewActions.setBulkOperationUpdate({
                                            bulkOperationUpdate: setInterval(() => {
                                                this.store.dispatch(MainViewActions.runBulkOperationUpdate())
                                            }, 30000)
                                        })
                                    }
                                    return MainViewActions.noAction()
                                } else {
                                    if (bulkOperationUpdate) {
                                        clearInterval(bulkOperationUpdate)

                                        return MainViewActions.setBulkOperationUpdate({
                                            bulkOperationUpdate: null
                                        })
                                    }

                                    return MainViewActions.noAction()
                                }
                            })
                        )
                    })
                )
            })
        ) })

        this.toggleShowBulkOperationsOff$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.toggleShowBulkOperationsOff),
            switchMap(() => {
                this.store.dispatch(MainViewActions.setShowBulkOperations({
                    showBulkOperations: false
                }))

                return this.store.select(selectBulkOperationUpdate).pipe(
                    take(1),
                    map(bulkOperationUpdate => {
                        if (bulkOperationUpdate) {
                            clearInterval(bulkOperationUpdate)

                            return MainViewActions.setBulkOperationUpdate({
                                bulkOperationUpdate: null
                            })
                        }

                        return MainViewActions.noAction()
                    })
                )
            })
        ) })

        this.setNotificationDetail$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.setNotificationDetail),
            map(action => {
                if (action.detail) {
                    this.mainDialogService.showNotificationDetailDialog()
                }

                return MainViewActions.setNotificationDetailInternal({
                    detail: action.detail
                })
            })
        ) })

        this.setUnreadNotificationsCount$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.setUnreadNotificationsCount),
            switchMap((action) => {
                return this.store.select(selectUnreadNotificationsCount).pipe(
                    take(1),
                    map((oldCount) => {
                        if (action.count > oldCount) {
                            this.store.dispatch(HeaderActions.filter({
                                key: StoreKeysEnum.HeaderComponent + ':unread'
                            }))
                        }

                        return MainViewActions.setUnreadNotificationsCountInternal({ count: action.count })
                    })
                )

            })
        ) })

        this.setBlockRequests$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.setBlockRequests),
            switchMap((action) => {
                const SERVICE_UNAVAILABLE_MESSAGE_ID = -1
                const SERVICE_UNAVAILABLE_MESSAGE_WRITE = 'Jsou přechodně zablokované požadavky na zápis dat aplikace!'
                const SERVICE_UNAVAILABLE_MESSAGE_ALL = 'Jsou přechodně zablokované všechny požadavky aplikace!'

                return this.store.select(selectMessages).pipe(
                    take(1),
                    map((messages) => {
                        if (action.blockRequests === 'NONE' && _.find(messages,
                            ['id', SERVICE_UNAVAILABLE_MESSAGE_ID])) {
                            // odstrani zpravu, pokud uz neni blokujici stav a zprava existuje
                            this.store.dispatch(MainViewActions.setImportantMessages({
                                messages: messages.filter(message => message.id !== SERVICE_UNAVAILABLE_MESSAGE_ID)
                            }))

                            // reload state to load departments and other project related data which could be blocked on first app loading
                            window.location.reload()

                            return MainViewActions.noAction()
                        } else if (action.blockRequests !== 'NONE' && !_.find(messages,
                            ['id', SERVICE_UNAVAILABLE_MESSAGE_ID])) {

                            // prida zpravu, pokud je blokujici stav a zprava neexistuje
                            return MainViewActions.setImportantMessages({
                                messages: _.concat(messages, {
                                    id: SERVICE_UNAVAILABLE_MESSAGE_ID,
                                    content: action.blockRequests === 'ALL' ? SERVICE_UNAVAILABLE_MESSAGE_ALL
                                        : SERVICE_UNAVAILABLE_MESSAGE_WRITE
                                })
                            })
                        } else {
                            return MainViewActions.noAction()
                        }
                    })
                )
            })
        ) })

        this.setCuzkPasswordValidityState$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.setCuzkPasswordValidityState),
            switchMap((action) => {
                const EXPIRATED_MESSAGE_ID = -2
                const EXPIRATED_MESSAGE_TEXT =
                    'Vypršela platnost hesla do dálkového přístupu ČÚZK, při aktualizaci dat nebude možné stáhnout osobní údaje. '
                    + 'Změňte si heslo v ČÚZK (<a href=\'https://cleverfarm.atlassian.net/wiki/spaces/SDAS/pages/2153775131/\' target=\'_blank\'>návod</a>) '
                    + 'a následně nové heslo zadejte do aplikace CleverAssets.'
                const EXPIRATING_MESSAGE_ID = -3
                const EXPIRATING_MESSAGE_TEXT =
                    'Zbývající počet dnů platnosti hesla do dálkového přístupu ČÚZK je '
                    + action.cuzkPasswordValidityState
                    + ', po vypršení platnosti hesla nebude možné stáhnout osobní údaje při aktualizaci dat. '
                    + 'Změňte si heslo v ČÚZK (<a href=\'https://cleverfarm.atlassian.net/wiki/spaces/SDAS/pages/2153775131/\' target=\'_blank\'>návod</a>) '
                    + 'a následně nové heslo zadejte do aplikace CleverAssets.'

                return this.store.select(selectMessages).pipe(
                    take(1),
                    map((messages) => {
                        // pokud je heslo v pořádku nebo nevíme a je tam zpráva -2 nebo -3, tak je odstraň
                        if ((_.isNil(action.cuzkPasswordValidityState) || action.cuzkPasswordValidityState === 0) && _.find(messages,
                            (message) => message.id === EXPIRATED_MESSAGE_ID || message.id === EXPIRATING_MESSAGE_ID)
                        ) {
                            this.store.dispatch(MainViewActions.setImportantMessages({
                                messages: _.filter(messages, (message) => message.id === EXPIRATED_MESSAGE_ID && message.id === EXPIRATING_MESSAGE_ID)
                            }))
                        }

                        // pokud platnost hesla vypršela a není tam zpráva -2 nebo tam je zpráva -3, tak
                        if (!_.isNil(action.cuzkPasswordValidityState)
                            && action.cuzkPasswordValidityState === -1
                            && (!_.find(messages, (message) => message.id === EXPIRATED_MESSAGE_ID)
                                || _.find(messages, (message) => message.id === EXPIRATING_MESSAGE_ID))
                        ) {
                            // pokud je tam zpráva -2
                            _.find(messages, (message) => message.id === EXPIRATED_MESSAGE_ID) ?
                                // jenom odstran zprávu -3
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.filter(messages, (message) => message.id !== EXPIRATING_MESSAGE_ID)
                                })) :
                                // nebo odstran zprávu -3, ale také přidej zprávu -2
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.concat(
                                        _.filter(messages, (message) => message.id !== EXPIRATING_MESSAGE_ID),
                                        {
                                            id: EXPIRATED_MESSAGE_ID,
                                            content: EXPIRATED_MESSAGE_TEXT
                                        })
                                }))
                        }

                        // pokud platnost hesla brzy vyprší a není tam zpráva -3 se správným textem nebo tam je zpráva -2, tak
                        if (!_.isNil(action.cuzkPasswordValidityState)
                            && action.cuzkPasswordValidityState >= 1
                            && (!_.find(messages, (message) => message.id === EXPIRATING_MESSAGE_ID && message.content === EXPIRATING_MESSAGE_TEXT)
                                || _.find(messages, (message) => message.id === EXPIRATED_MESSAGE_ID))
                        ) {
                            // pokud je tam zpráva -3 se správným textem
                            _.find(messages, (message) => message.id === EXPIRATING_MESSAGE_ID && message.content === EXPIRATING_MESSAGE_TEXT) ?
                                // jenom odstran zprávu -2
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.filter(messages, (message) => message.id !== EXPIRATED_MESSAGE_ID)
                                })) :
                                // nebo odstran zprávu -2 a -3 a také přidej zprávu -3 se správným textem
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.concat(
                                        _.filter(
                                            _.filter(messages, (message) => message.id !== EXPIRATING_MESSAGE_ID),
                                            (message) => message.id !== EXPIRATED_MESSAGE_ID),
                                        {
                                            id: EXPIRATING_MESSAGE_ID,
                                            content: EXPIRATING_MESSAGE_TEXT
                                        })
                                }))
                        }

                        return MainViewActions.noAction()
                    })
                )
            })
        )
        })

        this.setSkippedDateDueCuzkPasswordValidity$ = createEffect(() => { return this.actions$.pipe(
            ofType(MainViewActions.setSkippedDateDueCuzkPasswordValidity),
            switchMap((action) => {
                const SKIPPED_MESSAGE_ID = -4
                const SKIPPED_MESSAGE_TEXT = action.skippedDateDueCuzkPasswordValidity != null ?
                    'Jsou připravena data pro aktualizaci. Z důvodu neplatných přihlašovacích údajů k ČÚZK byla aktualizace dat odložena. '
                    + 'Zadejte aktuální přihlašovací údaje. Jinak dne '
                    + format(addDays(parseISO(action.skippedDateDueCuzkPasswordValidity), 10), 'd.M.y')
                    + ' bude aktualizace spuštěna bez aktualizace osobních údajů.' : ''

                return this.store.select(selectMessagesAndCuzkPasswordValidityState).pipe(
                    take(1),
                    map(({messages, cuzkPasswordValidityState}) => {
                        // pokud je datum null a je tam zpráva -4, tak ji odstraň
                        if (_.isNil(action.skippedDateDueCuzkPasswordValidity) && _.find(messages, (message) => message.id === SKIPPED_MESSAGE_ID)) {
                            this.store.dispatch(MainViewActions.setImportantMessages({
                                messages: _.filter(messages, (message) => message.id !== SKIPPED_MESSAGE_ID)
                            }))
                        }

                        if (!_.isNil(action.skippedDateDueCuzkPasswordValidity)) {
                            // pokud je spravne zadane heslo, smaz message id -4
                            if (_.isNil(cuzkPasswordValidityState)) {
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.filter(messages, (message) => message.id !== SKIPPED_MESSAGE_TEXT)
                                }))
                            // pokud datum není null a není tam zpráva -4, která by měla stejný text
                            } else if (!_.find(messages, (message) => message.id === SKIPPED_MESSAGE_ID && message.content === SKIPPED_MESSAGE_TEXT)) {
                                // odstran zprávu -4 a přidej zprávu -4 se správným textem
                                this.store.dispatch(MainViewActions.setImportantMessages({
                                    messages: _.concat(
                                        _.filter(messages, (message) => message.id !== SKIPPED_MESSAGE_ID),
                                        {
                                            id: SKIPPED_MESSAGE_ID,
                                            content: SKIPPED_MESSAGE_TEXT
                                        })
                                }))
                            }
                        }

                        return MainViewActions.noAction()
                    })
                )
            })
        ) })

        this.setAllIsEditingFalse$ = createEffect(() => this.actions$.pipe(
            ofType(MainViewActions.setAllIsEditingFalse),
            concatMap(() => {
                return [
                    SubcontractEditActions.setIsEditingContract({isEditingContract: false}),
                    ContractEditActions.setIsEditingContract({isEditingContract: false}),
                    BarterActions.setBarterIsEditing({ value: false }),
                    SubjectShowActions.setIsEditingSubject({value: false}),
                    NonCadastralContractCreateActions.setIsEditingContractFalse(),
                    CaseEditActions.setCaseIsEditing({ isEditing: false }),
                ]
            })
        ))

        this.setSelectedDepartment$ = createEffect(() => this.actions$.pipe(
            ofType(MainViewActions.setSelectedDepartment),
            map((action) => {
                this.selectedDepartmentService.saveLastSelectedDepartment(action.department)

                return MainViewActions.setSelectedDepartmentInternal({
                    department: action.department
                })
            })
        ))
    }
}